import {
  isValidPassengerNationalityCode,
  tryResolveNationalityToIso2,
} from "../../../utils/countries/flightNationalityOptions";
import { isValidEmail } from "../../../utils/security";
import { isEnglishOnlyPersonName } from "../../../utils/passengers/englishOnlyPersonName";

/** Same mapping as `buildPublicStyleServiceTag` / public `buildServiceTag`. */
const CHARTER_FLIGHT_CATEGORY_RE = /charter|צ'רטר|שכר/i;

export const FLIGHT_PASSPORT_FIELD_KEYS = new Set([
  "passportNumber",
  "passportExpiry",
]);

/** שדות טופס נוסעי טיסה — תואם payload המוקאב / LDS merge */
export const FLIGHT_PASSENGER_FORM_FIELDS = [
  { key: "firstName", label: "שם פרטי", type: "text", required: true },
  { key: "lastName", label: "שם משפחה", type: "text", required: true },
  {
    key: "dateOfBirth",
    label: "תאריך לידה (DD/MM/YYYY)",
    type: "text",
    required: true,
    placeholder: "למשל 15/03/1990",
  },
  { key: "gender", label: "מגדר", type: "select", required: true },
  { key: "passportNumber", label: "מספר דרכון", type: "text", required: true },
  {
    key: "passportExpiry",
    label: "תוקף דרכון (DD/MM/YYYY)",
    type: "text",
    required: true,
    placeholder: "למשל 01/01/2030",
  },
  { key: "nationality", label: "אזרחות", type: "nationality", required: true },
  {
    key: "cell",
    label: "מספר טלפון (נוסע ראשי)",
    type: "tel",
    required: true,
    primaryOnly: true,
    placeholder: "למשל 050-1234567",
  },
  {
    key: "email",
    label: "דוא״ל (נוסע ראשי)",
    type: "email",
    required: true,
    primaryOnly: true,
    placeholder: "למשל name@example.com",
    autoComplete: "email",
  },
];

export function getFlightPassengerFormFields({ requirePassport = true } = {}) {
  if (requirePassport) return FLIGHT_PASSENGER_FORM_FIELDS;
  return FLIGHT_PASSENGER_FORM_FIELDS.filter(
    (field) => !FLIGHT_PASSPORT_FIELD_KEYS.has(field.key)
  );
}

export const FLIGHT_PASSENGER_DATE_FIELD_KEYS = new Set([
  "dateOfBirth",
  "passportExpiry",
]);

export function flightPassengerDateHintKey(passengerIndex, fieldKey) {
  return `${passengerIndex}:${fieldKey}`;
}

export function isFlightPassengerDdMmYyyy(s) {
  const p = String(s || "").trim().split("/");
  if (p.length !== 3) return false;
  const [d, m, y] = p.map(Number);
  if (!d || !m || !y) return false;
  return d >= 1 && d <= 31 && m >= 1 && m <= 12 && y >= 1900 && y <= 2100;
}

function pushCategoryHint(hints, value) {
  const text = String(value ?? "").trim();
  if (text) hints.push(text);
}

function collectCategoryHintsFromDeal(deal) {
  const hints = [];
  if (!deal || typeof deal !== "object") return hints;
  pushCategoryHint(hints, deal.ticketType);
  pushCategoryHint(hints, deal.flightCategory);
  const linked = deal.linkedFlights ?? deal.linkedflights;
  const flightsRaw = linked?.flight;
  const flights = Array.isArray(flightsRaw)
    ? flightsRaw
    : flightsRaw
      ? [flightsRaw]
      : [];
  for (const flight of flights) {
    const legsRaw = flight?.leg;
    const legs = Array.isArray(legsRaw) ? legsRaw : legsRaw ? [legsRaw] : [];
    for (const leg of legs) {
      pushCategoryHint(hints, leg?.flightCategory);
      pushCategoryHint(hints, leg?.ticketType);
    }
  }
  return hints;
}

export function collectFlightCategoryHints(offerOrDeal) {
  const hints = [];
  if (!offerOrDeal || typeof offerOrDeal !== "object") return hints;
  pushCategoryHint(hints, offerOrDeal.ticketType);
  pushCategoryHint(hints, offerOrDeal.flightCategory);
  hints.push(...collectCategoryHintsFromDeal(offerOrDeal.rawDeal || offerOrDeal));
  hints.push(
    ...collectCategoryHintsFromDeal(offerOrDeal.selfComposeLegs?.outboundRawDeal)
  );
  hints.push(
    ...collectCategoryHintsFromDeal(offerOrDeal.selfComposeLegs?.inboundRawDeal)
  );
  return hints;
}

export function isCharterFlightCategory(value) {
  return CHARTER_FLIGHT_CATEGORY_RE.test(String(value || "").trim());
}

export function isCharterFlightOffer(offerOrDeal) {
  return collectFlightCategoryHints(offerOrDeal).some(isCharterFlightCategory);
}

/**
 * Agent extras/passengers: hide passport on charter.
 * Unknown category on this inventory defaults to hide (charter-only in practice).
 * Explicit scheduled/regular still requires passport.
 */
export function shouldRequirePassportOnAgentFlightForm(offerOrDeal) {
  if (isCharterFlightOffer(offerOrDeal)) return false;
  return collectFlightCategoryHints(offerOrDeal).some(
    (hint) => !isCharterFlightCategory(hint)
  );
}

/**
 * @param {object} p
 * @param {number} index
 * @param {{ requirePassport?: boolean }} [options]
 */
/**
 * @param {object} p
 * @param {number} index
 * @param {{ requirePassport?: boolean, requireDateOfBirth?: boolean, requireGender?: boolean }} [options]
 * @returns {string|null}
 */
export function getFlightPassengerPaxValidationMessage(p, index, options = {}) {
  const requirePassport = options.requirePassport !== false;
  const requireDateOfBirth = options.requireDateOfBirth !== false;
  const requireGender = options.requireGender !== false;
  const n = (index ?? 0) + 1;
  if (!String(p?.firstName || "").trim()) {
    return `נא למלא שם פרטי לנוסע ${n}`;
  }
  if (!String(p?.lastName || "").trim()) {
    return `נא למלא שם משפחה לנוסע ${n}`;
  }
  if (!isEnglishOnlyPersonName(p.firstName) || !isEnglishOnlyPersonName(p.lastName)) {
    return `שם פרטי ומשפחה לנוסע ${n} חייבים להיות באנגלית בלבד (כפי בדרכון)`;
  }
  if (requireGender && !String(p?.gender || "").trim()) {
    return `נא לבחור מין לנוסע ${n}`;
  }
  if (!isValidPassengerNationalityCode(p?.nationality)) {
    return `נא לבחור אזרחות תקינה לנוסע ${n}`;
  }
  if (requireDateOfBirth && !isFlightPassengerDdMmYyyy(p?.dateOfBirth)) {
    return `נא למלא תאריך לידה תקין (DD/MM/YYYY) לנוסע ${n}`;
  }
  if (requirePassport) {
    if (!String(p?.passportNumber || "").trim()) {
      return `נא למלא מספר דרכון לנוסע ${n}`;
    }
    if (!isFlightPassengerDdMmYyyy(p?.passportExpiry)) {
      return `נא למלא תוקף דרכון תקין (DD/MM/YYYY) לנוסע ${n}`;
    }
  }
  if (index === 0) {
    if (!String(p?.cell || "").trim()) {
      return "נא למלא טלפון נייד לנוסע הראשי";
    }
    if (!isValidEmail(String(p?.email || "").trim())) {
      return "נא למלא דוא״ל תקין לנוסע הראשי";
    }
  }
  return null;
}

export function validateFlightPassengerPax(p, index, options = {}) {
  return getFlightPassengerPaxValidationMessage(p, index, options) == null;
}

/**
 * נרמול אזרחות כמו בשלב נוסעים (ISO2 / resolve מטקסט).
 * @returns {{ next: object[], changed: boolean }}
 */
export function normalizePassengersNationality(passengers) {
  const arr = Array.isArray(passengers) ? passengers : [];
  const next = arr.map((p) => {
    const n = p.nationality;
    if (n == null || String(n).trim() === "") return p;
    if (isValidPassengerNationalityCode(n)) {
      const u = String(n).toUpperCase();
      return u !== n ? { ...p, nationality: u } : p;
    }
    const r = tryResolveNationalityToIso2(n);
    return r ? { ...p, nationality: r } : p;
  });
  const changed = next.some(
    (p, i) => p.nationality !== arr[i]?.nationality
  );
  return { next, changed };
}

export function flightPassengerGenderLabelHe(v) {
  if (v === "M") return "זכר";
  if (v === "F") return "נקבה";
  if (v === "X") return "אחר";
  return "—";
}
